import { InputRef, Form, Input, InputNumber, Switch, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { PickerRef } from 'rc-picker';
import {
  PropsWithChildren,
  useState,
  useContext,
  useRef,
  useEffect,
} from 'react';

import { Item } from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer/types';
import {
  getDisabledTimeForShift,
  setTimeRangeDate,
} from 'business/shift-report/report/pages/ReportEdit/components/new-task-drawer/services';
import { EditableContext } from 'business/shift-report/report/pages/ReportEdit/components/validation-editable-row';
import { parseReportDateString } from 'business/shift-report/report/services/timeOperations';
import logger from 'technical/logger';

interface EditableCellProps {
  title: React.ReactNode;
  editable?: 'number' | 'hour' | 'boolean' | 'text';
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

export const ValidationEditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}: PropsWithChildren<EditableCellProps>) => {
  const [editing, setEditing] = useState(false);
  const form = useContext(EditableContext)!;

  const inputRef = useRef<InputRef>(null);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const switchRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<PickerRef>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      numberInputRef.current?.focus();
      switchRef.current?.focus();
      pickerRef.current?.focus();
    }
  }, [editing]);

  const startEditing = () => {
    setEditing(true);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const stopEditing = () => {
    setEditing(false);
  };

  const save = () => {
    try {
      const values = form.getFieldsValue();
      const updatedRecord = { ...record, ...values };

      handleSave(updatedRecord);
    } catch (error) {
      logger.error('Task mass validation save error', {
        error,
        reportId: record.report.id,
        taskId: record.key,
      });
    }

    stopEditing();
  };

  const saveTime = async () => {
    try {
      const startDate = form.getFieldValue('start');
      const endDate = form.getFieldValue('end');

      const [fixedStartDate, fixedEndDate] = setTimeRangeDate(
        dayjs(record.report.date),
        record.report.shift,
      )(startDate ?? record.start, endDate ?? record.end);

      const updatedRecord = {
        ...record,
        start: fixedStartDate ?? record.start,
        end: fixedEndDate ?? record.end,
      };

      handleSave(updatedRecord);
    } catch (error) {
      logger.error('Task mass validation save error', {
        error,
        reportId: record.report.id,
        taskId: record.key,
      });
    }

    stopEditing();
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0, width: 'auto' }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        {editable === 'text' ? (
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            size="small"
          />
        ) : null}
        {editable === 'number' ? (
          <InputNumber
            ref={numberInputRef}
            onPressEnter={save}
            onBlur={save}
            size="small"
          />
        ) : null}
        {editable === 'boolean' ? (
          <Switch ref={switchRef} onChange={save} size="small" />
        ) : null}
        {editable === 'hour' ? (
          <TimePicker
            ref={pickerRef}
            onChange={() => saveTime()}
            size="small"
            format="HH:mm"
            needConfirm={false}
            allowClear={false}
            showNow={false}
            hideDisabledOptions
            disabledTime={() =>
              getDisabledTimeForShift(
                record.report.shift,
                parseReportDateString(record.report?.date),
              )
            }
          />
        ) : null}
      </Form.Item>
    ) : (
      <div style={{ paddingInlineEnd: 24 }} onClick={startEditing}>
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
